import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { StaticPanel } from 'src/components/StaticPanel';
import { ToneOfVoiceMenu } from 'src/components/ToneOfVoiceMenu';
import { isSameLanguage } from 'src/data/generation';
import { ROTATE_DIRECTION } from 'src/data/ui';
import { STONLY_ID } from 'src/lib/stonly';
import { useIsApplicationModeInfinity } from 'src/services';
import { useCurrentProject, useProjectActions } from 'src/store';

import { ToneOfVoiceLabel } from './ToneOfVoiceLabel';
import { useFallbackToDefaultTov } from './useFallbackToDefaultTov';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const BlockToneOfVoice = ({
  className,
  classes,
  title,
  generationTool,
  onSelect,
  disabled,
  stateVars,
  onChange,
  emptyDefaultValue,
  language,
}) => {
  const styles = useStyles({ classes });

  const [rootElement, setRootElement] = useState(null);
  const initialLanguage = useRef(language);

  const isInfinityMode = useIsApplicationModeInfinity();
  const projectActions = useProjectActions();
  const currentProject = useCurrentProject();

  const {
    toneOfVoiceMenuOpened,
    toneOfVoices,
    customModels,
    selectedTov,
    selectedCustomModelId,
    draftToneOfVoice,
    toneOfVoiceError,
  } = Object.assign(useViewState(), stateVars);

  const {
    handleTovMenuOpen,
    handleTovMenuClose,
    handleMenuSelect,
    handleAddToneOfVoice,
    handleSelectCustomModel,
    handleSelectDraftToneOfVoice,
    handleOpenBrandVoicePage,
  } = useHandlers({
    onSelect,
    generationTool,
    onChange,
    language,
  });

  useEffect(() => {
    // check if currently selected is in the correct language. If not, clear the selection
    const customModel = customModels.find((cm) => cm.id === selectedCustomModelId);
    const toneOfVoice = toneOfVoices.find((tov) => tov.id === selectedTov);
    const tovLanguage = selectedCustomModelId
      ? customModel?.brandVoiceLanguage
      : toneOfVoice?.brandVoiceLanguage;

    if (tovLanguage && !isSameLanguage(tovLanguage, language)) {
      projectActions.clearTovInputs(emptyDefaultValue);
    }
  }, [
    language,
    selectedTov,
    selectedCustomModelId,
    projectActions,
    emptyDefaultValue,
    toneOfVoices,
    customModels,
  ]);

  useFallbackToDefaultTov({
    selectedCustomModelId,
    emptyDefaultValue,
    projectType: currentProject?.type,
  });

  useEffect(() => {
    if (initialLanguage.current !== language) {
      projectActions.clearDraftToneOfVoice();
      projectActions.clearSelectedCustomModelId();
      initialLanguage.current = language;
    }
  }, [language, projectActions]);

  return (
    <StaticPanel
      className={cx(styles.root, className)}
      classes={{ title: styles.title }}
      title={title || 'Tone of voice'}
      titleStonlyId={STONLY_ID.toneOfVoice}
      ref={setRootElement}
    >
      <div
        className={cx(styles.tovPicker, {
          [styles.disabled]: disabled,
          [styles.active]: toneOfVoiceMenuOpened,
          [styles.error]: !!toneOfVoiceError,
        })}
        onClick={disabled ? null : handleTovMenuOpen}
      >
        <ToneOfVoiceLabel
          toneOfVoices={toneOfVoices}
          customModels={customModels}
          selectedTov={selectedTov}
          selectedCustomModelId={selectedCustomModelId}
          disabled={disabled}
          draftToneOfVoice={draftToneOfVoice}
          language={language}
        />

        <ChevronIcon
          className={styles.icon}
          direction={toneOfVoiceMenuOpened ? ROTATE_DIRECTION.right : ROTATE_DIRECTION.down}
        />
      </div>
      <ToneOfVoiceMenu
        onClose={handleTovMenuClose}
        toneOfVoices={toneOfVoices}
        customModels={customModels}
        onSelect={handleMenuSelect}
        onSelectCustomModel={handleSelectCustomModel}
        selectedTov={selectedTov}
        selectedCustomModelId={selectedCustomModelId}
        disabled={disabled}
        onAddToneOfVoice={handleAddToneOfVoice}
        isMenuOpen={toneOfVoiceMenuOpened}
        generationTool={generationTool}
        referenceElement={rootElement}
        isInfinityMode={isInfinityMode}
        draftToneOfVoice={draftToneOfVoice}
        onSelectDraftToneOfVoice={handleSelectDraftToneOfVoice}
        onOpenBrandVoicePage={handleOpenBrandVoicePage}
        language={language}
      />
      {toneOfVoiceError && (
        <CustomTypography
          className={styles.error}
          variant={TYPOGRAPHY_VARIANT.regular12}
          color="error"
        >
          {toneOfVoiceError}
        </CustomTypography>
      )}
    </StaticPanel>
  );
};

BlockToneOfVoice.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  title: PropTypes.string,
  generationTool: PropTypes.string,
  stateVars: PropTypes.shape({}),
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  emptyDefaultValue: PropTypes.bool,
  language: PropTypes.string,
};
BlockToneOfVoice.defaultProps = {
  className: null,
  classes: null,
  title: null,
  generationTool: null,
  stateVars: null,
  disabled: false,
  onSelect: null,
  onChange: null,
  emptyDefaultValue: false,
  language: null,
};
